import { supabase } from '../lib/supabase';

export const orderService = {
  // Get all orders with optional filters
  async getAllOrders(filters = {}) {
    try {
              let query = supabase?.from('orders')?.select(`
            *,
            vehicle:vehicles(*),
            car_house:car_houses(name, code, city),
            assigned_technician:technicians!assigned_technician_id(first_name, last_name, email),
            created_by:user_profiles!created_by_id(full_name),
            service_type:service_types(*)
          `)?.order('created_at', { ascending: false });

      // Apply filters dynamically
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }
      if (filters?.car_house_id) {
        query = query?.eq('car_house_id', filters?.car_house_id);
      }
      if (filters?.assigned_technician_id) {
        query = query?.eq('assigned_technician_id', filters?.assigned_technician_id);
      }
      if (filters?.search) {
        query = query?.or(`order_number.ilike.%${filters?.search}%,customer_name.ilike.%${filters?.search}%,title.ilike.%${filters?.search}%`);
      }

      // Apply date filters
      if (filters?.date_from) {
        query = query?.gte('created_at', filters?.date_from);
      }
      if (filters?.date_to) {
        query = query?.lte('created_at', filters?.date_to);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Post-process to attach dealership-specific effective price
      const orders = data || [];

      // Collect distinct dealership and service type IDs
      const dealershipIds = Array.from(new Set(orders.map(o => o?.car_house_id).filter(Boolean)));
      const serviceTypeIds = Array.from(new Set(orders.map(o => o?.service_type_id).filter(Boolean)));

      let pricingMap = new Map();
      if (dealershipIds.length > 0 && serviceTypeIds.length > 0) {
        const { data: pricingRows, error: pricingError } = await supabase
          ?.from('dealership_service_pricing')
          ?.select('dealership_id, service_type_id, price, is_active')
          ?.in('dealership_id', dealershipIds)
          ?.in('service_type_id', serviceTypeIds);

        if (!pricingError && Array.isArray(pricingRows)) {
          pricingMap = new Map(
            pricingRows.map(row => [
              `${row.dealership_id}:${row.service_type_id}`,
              { price: row.price, is_active: row.is_active }
            ])
          );
        }
      }

      const enriched = orders.map(o => {
        // Since total_amount is now always calculated from dealership_service_pricing table,
        // we can simply use the total_amount from the database
        const effectivePrice = o?.total_amount ?? null;
        console.log(`Order ${o.order_number}: Using database total_amount ${effectivePrice}€`);
        return { ...o, effective_price: effectivePrice };
      });

      return { data: enriched, error: null };
    } catch (error) {
      console.error('Error fetching orders:', error);
      return { data: null, error };
    }
  },

  // Get order by ID with detailed information
  async getOrderById(id) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          vehicle:vehicles(*),
          car_house:car_houses(*),
          assigned_technician:technicians!assigned_technician_id(*),
          created_by:user_profiles!created_by_id(*),
          order_service_items(
            *,
            service_item:service_items(*)
          ),
          attachments(*),
          audit_logs(
            *,
            user:user_profiles(full_name, email)
          )
        `)?.eq('id', id)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching order:', error);
      return { data: null, error };
    }
  },

  // Create new order
  async createOrder(orderData, userId) {
    try {
      
      // Ensure we're not passing any ID field
      const { id, ...cleanOrderData } = orderData;
      if (id) {
        console.warn('Removing ID from order data:', id);
      }
      
      const finalOrderData = {
        ...cleanOrderData,
        created_by_id: userId
      };

      // Let the database calculate the price using the dealership_service_pricing table
      // The database trigger will automatically set the correct total_amount
      // Remove any manually provided total_amount to ensure it's calculated from the database
      delete finalOrderData.total_amount;
      
      console.log('Order will be created with pricing calculated from dealership_service_pricing table');
      console.log(`Dealership ID: ${finalOrderData.car_house_id}`);
      console.log(`Service Type ID: ${finalOrderData.service_type_id}`);
      console.log('Database trigger will automatically calculate the correct price');
      
      
      const { data, error } = await supabase?.from('orders')?.insert([finalOrderData])?.select(`
          *,
          vehicle:vehicles(*),
          car_house:car_houses(*)
        `)?.single();


      if (error) throw error;

      // Create audit log
      if (data) {
        await this.createAuditLog({
          user_id: userId,
          order_id: data?.id,
          action: 'created',
          table_name: 'orders',
          record_id: data?.id,
          new_values: orderData
        });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating order:', error);
      return { data: null, error };
    }
  },

  // Update order
  async updateOrder(id, updates, userId) {
    try {
      
      // Get current data for audit log
      const { data: currentData } = await supabase?.from('orders')?.select('*')?.eq('id', id)?.single();

      // Log the specific update being attempted

      // If status is being changed to 'completed', handle completion logic
      if (updates?.status === 'completed' && currentData?.status !== 'completed') {
        
        // Set actual completion time if not already set
        if (!currentData?.actual_completion) {
          updates.actual_completion = new Date().toISOString();
        }
        
        // Always preserve the existing total amount first
        if (currentData?.total_amount && !updates.total_amount) {
          updates.total_amount = currentData.total_amount;
        }
        
        // Try to calculate order totals using the simple function
        try {
          const { data: totalData, error: totalError } = await supabase?.rpc('calculate_order_totals_simple', {
            order_uuid: id
          });
          
          if (totalError) {
            console.error('Error calculating order totals:', totalError);
          } else if (totalData && parseFloat(totalData) > 0) {
            // Only update if we got a valid positive total
            updates.total_amount = parseFloat(totalData);
          } else {
          }
        } catch (totalError) {
          console.error('Exception calculating order totals:', totalError);
        }
      }

      // If Autohaus or Service-Typ changed and no explicit total_amount provided, compute dealership price
      try {
        const carHouseChanged = Object.prototype.hasOwnProperty.call(updates || {}, 'car_house_id');
        const serviceTypeChanged = Object.prototype.hasOwnProperty.call(updates || {}, 'service_type_id');
        const totalProvided = Object.prototype.hasOwnProperty.call(updates || {}, 'total_amount');

        const nextCarHouseId = carHouseChanged ? updates?.car_house_id : currentData?.car_house_id;
        const nextServiceTypeId = serviceTypeChanged ? updates?.service_type_id : currentData?.service_type_id;

        if (!totalProvided && nextCarHouseId && nextServiceTypeId && (carHouseChanged || serviceTypeChanged)) {
          // Try dealership-specific price
          const { data: pricingRow, error: pricingErr } = await supabase
            ?.from('dealership_service_pricing')
            ?.select('price, is_active')
            ?.eq('dealership_id', nextCarHouseId)
            ?.eq('service_type_id', nextServiceTypeId)
            ?.maybeSingle();
          if (!pricingErr && pricingRow && (pricingRow.is_active === true || pricingRow.is_active === null || pricingRow.is_active === undefined)) {
            if (pricingRow.price !== null && pricingRow.price !== undefined) {
              updates.total_amount = parseFloat(pricingRow.price);
            }
          }
          // Fallback to service type price
          if (!Object.prototype.hasOwnProperty.call(updates, 'total_amount')) {
            const { data: serviceType, error: stErr } = await supabase
              ?.from('service_types')
              ?.select('price')
              ?.eq('id', nextServiceTypeId)
              ?.single();
            if (!stErr && serviceType && serviceType.price !== null && serviceType.price !== undefined) {
              updates.total_amount = parseFloat(serviceType.price);
            }
          }
        }
      } catch (pricingRecalcErr) {
        // Non-fatal; continue update
        console.warn('Could not recalc dealership price on update:', pricingRecalcErr);
      }

      const { data, error } = await supabase?.from('orders')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select(`
          *,
          vehicle:vehicles(*),
          car_house:car_houses(*),
          assigned_technician:technicians!assigned_technician_id(*)
        `)?.single();

      
      if (error) {
        console.error('Supabase update error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      // Create audit log
      if (data && currentData) {
        await this.createAuditLog({
          user_id: userId,
          order_id: id,
          action: 'updated',
          table_name: 'orders',
          record_id: id,
          old_values: currentData,
          new_values: updates
        });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating order:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      return { data: null, error };
    }
  },

  // Assign technician to order
  async assignTechnician(orderId, technicianId, userId) {
    try {
      const { data, error } = await supabase?.from('orders')?.update({
          assigned_technician_id: technicianId,
          status: 'assigned',
          updated_at: new Date()?.toISOString()
        })?.eq('id', orderId)?.select(`
          *,
          assigned_technician:technicians!assigned_technician_id(*)
        `)?.single();

      if (error) throw error;

      // Create notification for the assigned technician
      if (data && technicianId) {
        await this.createNotification({
          user_id: technicianId,
          order_id: orderId,
          type: 'order_assigned',
          title: 'New Order Assigned',
          message: `You have been assigned order ${data?.order_number}`
        });
      }

      // Create audit log
      await this.createAuditLog({
        user_id: userId,
        order_id: orderId,
        action: 'assigned',
        table_name: 'orders',
        record_id: orderId,
        new_values: { assigned_technician_id: technicianId }
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error assigning technician:', error);
      return { data: null, error };
    }
  },

  // Bulk assign technicians
  async bulkAssignTechnician(orderIds, technicianId, userId) {
    try {
      const { data, error } = await supabase?.from('orders')?.update({
          assigned_technician_id: technicianId,
          status: 'assigned',
          updated_at: new Date()?.toISOString()
        })?.in('id', orderIds)?.select('id, order_number');

      if (error) throw error;

      // Create notifications and audit logs for each order
      if (data && technicianId) {
        const notifications = data?.map(order => ({
          user_id: technicianId,
          order_id: order?.id,
          type: 'order_assigned',
          title: 'New Order Assigned',
          message: `You have been assigned order ${order?.order_number}`,
          created_at: new Date()?.toISOString()
        }));

        await supabase?.from('notifications')?.insert(notifications);

        // Create audit logs
        for (const order of data) {
          await this.createAuditLog({
            user_id: userId,
            order_id: order?.id,
            action: 'assigned',
            table_name: 'orders',
            record_id: order?.id,
            new_values: { assigned_technician_id: technicianId }
          });
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error bulk assigning technician:', error);
      return { data: null, error };
    }
  },

  // Get orders for technician mobile view
  async getTechnicianOrders(technicianId, filters = {}) {
    try {
      let query = supabase?.from('orders')?.select(`
          *,
          vehicle:vehicles(make, model, year, vin),
          car_house:car_houses(name),
          order_service_items(
            *,
            service_item:service_items(name, estimated_duration_hours)
          )
        `)?.eq('assigned_technician_id', technicianId)?.order('appointment_date', { ascending: true });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching technician orders:', error);
      return { data: null, error };
    }
  },

  // Calculate order totals using Supabase function
  async calculateOrderTotals(orderId) {
    try {
      const { data, error } = await supabase?.rpc('calculate_order_totals', {
          order_uuid: orderId
        });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error calculating order totals:', error);
      return { data: null, error };
    }
  },

  // Create audit log entry
  async createAuditLog(logData) {
    try {
      const { data, error } = await supabase?.rpc('create_audit_log', {
          p_user_id: logData?.user_id,
          p_order_id: logData?.order_id,
          p_action: logData?.action,
          p_table_name: logData?.table_name,
          p_record_id: logData?.record_id,
          p_old_values: logData?.old_values || null,
          p_new_values: logData?.new_values || null
        });

      return { data, error };
    } catch (error) {
      console.error('Error creating audit log:', error);
      return { data: null, error };
    }
  },

  // Create notification
  async createNotification(notificationData) {
    try {
      const { data, error } = await supabase?.from('notifications')?.insert([{
          ...notificationData,
          created_at: new Date()?.toISOString()
        }])?.single();

      return { data, error };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { data: null, error };
    }
  },

  // Get order statistics
  async getOrderStatistics(filters = {}) {
    try {
      let query = supabase?.from('orders')?.select('status, priority, total_amount, created_at, car_house_id');

      // Apply date filters
      if (filters?.date_from) {
        query = query?.gte('created_at', filters?.date_from);
      }
      if (filters?.date_to) {
        query = query?.lte('created_at', filters?.date_to);
      }
      if (filters?.car_house_id) {
        query = query?.eq('car_house_id', filters?.car_house_id);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Calculate statistics
      const stats = {
        total_orders: data?.length || 0,
        completed_orders: data?.filter(order => order?.status === 'completed')?.length || 0,
        in_progress_orders: data?.filter(order => order?.status === 'in_progress')?.length || 0,
        pending_orders: data?.filter(order => order?.status === 'pending')?.length || 0,
        total_revenue: data?.reduce((sum, order) => sum + (parseFloat(order?.total_amount) || 0), 0) || 0,
        avg_order_value: 0
      };

      stats.avg_order_value = stats?.total_orders > 0 ? stats?.total_revenue / stats?.total_orders : 0;

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching order statistics:', error);
      return { data: null, error };
    }
  },

  // Check technician availability
  async checkTechnicianAvailability(technicianId, appointmentDate) {
    try {
      const { data, error } = await supabase?.rpc('is_technician_available', {
          technician_uuid: technicianId,
          check_datetime: appointmentDate
        });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error checking technician availability:', error);
      return { data: false, error };
    }
  },

  // Delete order
  async deleteOrder(orderId, userId) {
    try {
      // Get the order data before deletion to find the VIN
      const { data: orderData, error: fetchError } = await supabase
        ?.from('orders')
        ?.select('vehicle_id')
        ?.eq('id', orderId)
        ?.single();

      if (fetchError) {
        console.error('Error fetching order data before deletion:', fetchError);
      }

      // Delete the order
      const { error } = await supabase?.from('orders')?.delete()?.eq('id', orderId);

      if (error) throw error;

      // Create audit log
      await this.createAuditLog({
        user_id: userId,
        order_id: orderId,
        action: 'deleted',
        table_name: 'orders',
        record_id: orderId,
        old_values: { order_id: orderId }
      });

      // Update scan history to mark as not having order
      if (orderData?.vehicle_id) {
        try {
          // Get the VIN from the vehicle
          const { data: vehicleData, error: vehicleError } = await supabase
            ?.from('vehicles')
            ?.select('vin')
            ?.eq('id', orderData.vehicle_id)
            ?.single();

          if (!vehicleError && vehicleData?.vin) {
            // Import scanHistoryService dynamically to avoid circular dependencies
            const { default: scanHistoryService } = await import('./scanHistoryService.js');
            await scanHistoryService.updateScanOrderStatus(vehicleData.vin, userId, false);
          }
        } catch (scanError) {
          console.error('Error updating scan history after order deletion:', scanError);
        }
      }

      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting order:', error);
      return { data: null, error };
    }
  }
};

export default orderService;